import { Article } from '../types/content'

export const articles: Article[] = [
  {
    slug: 'am-i-too-young',
    question: 'Am I too young for this?',
    category: 'Being believed',
    standfirst:
      'You are almost certainly not. Here is what the evidence actually says about when perimenopause starts, and why so many women in their late thirties are sent home.',
    readMinutes: 6,
    reviewedBy: 'Dr Imogen Halvorsen, MD, MSCP',
    reviewedOn: 'June 2026',
    body: [
      {
        heading: 'What the numbers say',
        paragraphs: [
          'The average age of menopause is 51. Perimenopause — the transition leading up to it — typically lasts four to eight years, which puts the start for most women somewhere in their mid-forties. For a substantial minority it starts in the late thirties. Around one in a hundred women reaches menopause before 40.',
          'So the honest answer to “am I too young” is: probably not, and the fact that you are asking usually means something has changed that you cannot explain.',
        ],
      },
      {
        heading: 'Why you were sent home',
        paragraphs: [
          'Most medical training programmes devote a matter of hours to menopause across an entire degree. Many clinicians finish training having never been taught to diagnose perimenopause clinically, and so they reach for the only tool they have: a blood test.',
          'That test is close to useless during perimenopause, for reasons we cover in a separate piece. The result is a loop familiar to almost everyone reading this — you describe real symptoms, the bloods come back normal, and you are told to manage your stress.',
        ],
      },
      {
        heading: 'What to bring instead',
        paragraphs: [
          'Three months of cycle lengths is worth more than any single hormone panel. Write down the first day of each period and count the gaps. A change of seven days or more in cycle length is a recognised clinical marker of the perimenopausal transition.',
          'Bring that, plus your three most disruptive symptoms and when in your cycle they land. It converts a vague conversation into a clinical picture, and it is very hard to dismiss.',
        ],
      },
    ],
    unknowns: [
      'We cannot predict how long your transition will last, or how severe it will be.',
      'There is no test that tells you when your final period will be.',
    ],
  },
  {
    slug: 'is-hrt-safe',
    question: 'Is hormone therapy safe, honestly?',
    category: 'Hormone therapy',
    standfirst:
      'The 2002 study that emptied menopause clinics worldwide has been substantially reinterpreted. Here is what it actually found, what it got wrong, and what the risk looks like in real numbers.',
    readMinutes: 11,
    reviewedBy: 'Dr Imogen Halvorsen, MD, MSCP',
    reviewedOn: 'June 2026',
    body: [
      {
        heading: 'What happened in 2002',
        paragraphs: [
          'The Women’s Health Initiative was halted early and reported in headlines as showing that hormone therapy causes breast cancer. Prescriptions fell by roughly 80% within a few years, and an entire generation of clinicians was trained in the aftermath to avoid the subject.',
          'The trial’s average participant was 63 years old — more than a decade past menopause — and the study used oral conjugated estrogens with a synthetic progestin, which is not what most women are prescribed today.',
        ],
      },
      {
        heading: 'What the risk looks like in numbers',
        paragraphs: [
          'For combined hormone therapy, the additional breast cancer risk reported in later analyses is on the order of one extra case per thousand women per year of use. For context, drinking two or more units of alcohol a day, or carrying significant excess weight after menopause, carry risks in a broadly similar range.',
          'Estrogen-only therapy, used by women who have had a hysterectomy, has not shown that increase — and in the WHI’s own estrogen-only arm, breast cancer incidence was slightly lower than placebo.',
          'Route matters. Transdermal estradiol — patch, gel or spray — does not carry the clot risk associated with oral estrogen, because it bypasses first-pass liver metabolism.',
        ],
      },
      {
        heading: 'The part nobody mentions',
        paragraphs: [
          'Risk was communicated relentlessly for two decades. Benefit was not. Starting hormone therapy within ten years of menopause is associated with reduced fracture risk, and observational data suggests cardiovascular benefit in that window.',
          'And symptom relief is itself a legitimate medical outcome. Untreated severe sleep loss and vasomotor symptoms are not cosmetic problems.',
        ],
      },
      {
        heading: 'Who it is genuinely not for',
        paragraphs: [
          'Current or recent breast cancer, active liver disease, unexplained vaginal bleeding, and a history of estrogen-dependent cancer all require a different conversation. So does a personal history of clot or stroke, though transdermal routes change that calculation.',
          'A good clinician will tell you which category you are in and why, in plain language, and will not simply refuse.',
        ],
      },
    ],
    unknowns: [
      'Long-term data past 10–15 years of continuous use remains limited.',
      'Whether hormone therapy is protective against dementia is genuinely unsettled — we do not prescribe it for that.',
      'Individual risk prediction is imprecise; population numbers are not the same as your number.',
    ],
  },
  {
    slug: 'why-normal-bloods-mean-nothing',
    question: 'Why did my bloods come back normal?',
    category: 'Perimenopause',
    standfirst:
      'Because in perimenopause a blood test measures one morning of a hormonal system that is swinging wildly. It is the wrong tool, and it is being used on almost everyone.',
    readMinutes: 7,
    reviewedBy: 'Dr Imogen Halvorsen, MD, MSCP',
    reviewedOn: 'May 2026',
    body: [
      {
        heading: 'The swing problem',
        paragraphs: [
          'During perimenopause, estradiol does not decline in a smooth line. It spikes higher than at any point in your reproductive life, then crashes, sometimes within the same week. FSH does the same in the opposite direction.',
          'Draw blood on a high day and everything looks unremarkable. Draw it four days later and it looks dramatic. Neither result changes what is happening to you.',
        ],
      },
      {
        heading: 'What we do test, and why',
        paragraphs: [
          'We test thyroid function, ferritin, vitamin D, HbA1c, and a lipid panel — not to diagnose perimenopause, but because thyroid disease, iron deficiency and insulin resistance mimic it closely and are all directly treatable.',
          'We test FSH only in specific circumstances: under 45 where premature ovarian insufficiency is a possibility, or where a hysterectomy makes cycle tracking impossible.',
        ],
      },
      {
        heading: 'How the diagnosis is actually made',
        paragraphs: [
          'Clinically. From your age, your cycle history, your symptom pattern, and the exclusion of the things that look similar. Major guidelines say this explicitly — for women over 45 with typical symptoms, no hormone testing is required at all.',
          'If someone tells you your labs rule out perimenopause, they are using a test outside the conditions it works in.',
        ],
      },
    ],
    unknowns: [
      'There is no validated blood test that confirms perimenopause. Anyone selling one is overstating it.',
      'AMH indicates ovarian reserve and can suggest proximity to menopause, but it cannot date it for an individual.',
    ],
  },
  {
    slug: 'brain-fog-not-dementia',
    question: 'Is this brain fog early dementia?',
    category: 'Mind',
    standfirst:
      'Almost certainly not. The word-finding failures and memory slips of perimenopause have a specific mechanism, a specific pattern, and — importantly — an end.',
    readMinutes: 8,
    reviewedBy: 'Dr Priya Raman, PhD',
    reviewedOn: 'April 2026',
    body: [
      {
        heading: 'What is happening',
        paragraphs: [
          'The brain is one of the most estrogen-dense organs in the body. The hippocampus and prefrontal cortex — verbal memory and executive function — are particularly rich in estrogen receptors, and they respond to withdrawal.',
          'Imaging studies during the transition show measurable changes in brain glucose metabolism that partially recover afterwards. The brain is recalibrating, not degenerating.',
        ],
      },
      {
        heading: 'How to tell the difference',
        paragraphs: [
          'Perimenopausal fog tends to be about retrieval: you know the word, you cannot reach it, and it arrives twenty minutes later. You walk into rooms and lose the thread. You can still learn new things and you can still follow a plot.',
          'Patterns that warrant assessment are different: losing recently formed memories entirely, getting lost somewhere familiar, or difficulty with steps of a task you have done for years. If that is what you are describing, get it looked at properly — but that is not what most women here are describing.',
        ],
      },
      {
        heading: 'What helps',
        paragraphs: [
          'Sleep, first and most. Fragmented sleep alone will produce cognitive symptoms indistinguishable from what you are experiencing, and it is usually treatable.',
          'Many women report meaningful improvement in fog on hormone therapy. The trial evidence for cognition specifically is mixed, and we will not oversell it.',
        ],
      },
    ],
    unknowns: [
      'Whether hormone therapy improves cognition directly, or improves it by fixing sleep, is not resolved.',
      'How long fog lasts varies enormously between women and we cannot predict it for you.',
    ],
  },
  {
    slug: 'sex-hurts',
    question: 'Why does sex hurt now, and can it stop?',
    category: 'Body',
    standfirst:
      'Yes, it can stop. This is one of the most treatable things on the entire list, and one of the least often raised — by patients or by clinicians.',
    readMinutes: 6,
    reviewedBy: 'Dr Ruth Adeyemi, MD',
    reviewedOn: 'May 2026',
    body: [
      {
        heading: 'It has a name',
        paragraphs: [
          'Genitourinary syndrome of menopause covers the whole cluster: dryness, burning, pain with sex, urinary urgency, and recurrent urinary tract infections. Around half of post-menopausal women have it. Somewhere under one in ten is treated for it.',
          'Unlike hot flushes, it does not resolve with time. Left alone it progresses, which is exactly why raising it early matters.',
        ],
      },
      {
        heading: 'What treats it',
        paragraphs: [
          'Local vaginal estrogen — cream, tablet or ring — restores tissue thickness, elasticity and natural lubrication over roughly eight to twelve weeks. Systemic absorption is minimal.',
          'Vaginal moisturisers used regularly are different from lubricants used during sex, and you want both. Pelvic floor physiotherapy addresses the protective muscle guarding that builds up after months of anticipating pain.',
        ],
      },
      {
        heading: 'If you have had breast cancer',
        paragraphs: [
          'This is where women are most often left with nothing. Major oncology and menopause bodies now support the use of low-dose vaginal estrogen in many breast cancer survivors, particularly where non-hormonal options have failed, after a discussion with your oncology team.',
          'It should be a conversation about your specific situation — not a blanket refusal at the door.',
        ],
      },
    ],
    unknowns: [
      'Long-term safety data for vaginal estrogen in women on aromatase inhibitors remains limited.',
      'Laser and radiofrequency devices are heavily marketed for this and the evidence does not currently justify the cost.',
    ],
  },
  {
    slug: 'weight-changed-shape',
    question: 'Why has my body changed shape when nothing else has?',
    category: 'Body',
    standfirst:
      'Because estrogen governs where fat is stored and how sensitive you are to insulin. This is a physiological redistribution, not evidence that you stopped trying.',
    readMinutes: 7,
    reviewedBy: 'Dr Priya Raman, PhD',
    reviewedOn: 'March 2026',
    body: [
      {
        heading: 'The redistribution',
        paragraphs: [
          'Through the menopause transition, fat storage shifts from hips and thighs toward the abdomen, including visceral fat around the organs. Studies tracking women through the transition show this happening independently of changes in total weight.',
          'At the same time, muscle mass declines with age unless it is actively defended, and muscle is where most glucose is disposed of. Less muscle plus lower insulin sensitivity is a metabolic double hit.',
        ],
      },
      {
        heading: 'What actually moves the needle',
        paragraphs: [
          'Resistance training two to three times a week, progressively loaded. Not walking, not more cardio — load. This is the single highest-yield intervention available in midlife and it is chronically under-prescribed to women.',
          'Protein intake most women find surprisingly high — in the region of 1.2 to 1.6 grams per kilogram of body weight daily — supports that muscle.',
        ],
      },
      {
        heading: 'On weight-loss medication',
        paragraphs: [
          'GLP-1 medications work in midlife women as they do elsewhere, and where there is a clinical indication we prescribe them. They also accelerate muscle loss if resistance training and protein are not in place, which matters more at this stage of life than at any other.',
          'We will not prescribe one as a first move to a woman whose actual problem is untreated sleep loss.',
        ],
      },
    ],
    unknowns: [
      'Whether hormone therapy prevents midlife weight gain is not established — it may influence distribution rather than total weight.',
      'Optimal protein targets in post-menopausal women are still debated.',
    ],
  },
  {
    slug: 'rage',
    question: 'Why am I so angry?',
    category: 'Mind',
    standfirst:
      'Because the hormone that acts as your natural brake has fallen away, and almost nobody warned you it would.',
    readMinutes: 5,
    reviewedBy: 'Dr Imogen Halvorsen, MD, MSCP',
    reviewedOn: 'April 2026',
    body: [
      {
        heading: 'The mechanism',
        paragraphs: [
          'Progesterone metabolises into allopregnanolone, which acts on GABA-A receptors — the same receptors targeted by benzodiazepines. It is, functionally, your body’s own anti-anxiety compound, and in perimenopause it is the first thing to go.',
          'Meanwhile erratic estrogen destabilises serotonin. The result is a nervous system with less buffering and less regulation, which is experienced as a shorter gap between something happening and your reaction to it.',
        ],
      },
      {
        heading: 'Why it feels like a character flaw',
        paragraphs: [
          'Because the anger is aimed at the people closest to you, over things that objectively do not warrant it, and you can see yourself doing it while it happens. That combination produces shame, and shame keeps women from mentioning it — including to us.',
          'It comes up in every circle we run, usually about forty minutes in, usually prefaced with “I have never told anyone this”.',
        ],
      },
      {
        heading: 'What helps',
        paragraphs: [
          'Micronised progesterone, taken at night, is often the specific intervention. Stabilising estrogen with transdermal estradiol helps the volatility.',
          'And naming it helps more than it sounds like it should. Women consistently report that understanding the mechanism reduces the distress before any prescription starts working.',
        ],
      },
    ],
    unknowns: [
      'Individual sensitivity to allopregnanolone withdrawal varies widely and we cannot predict who will be affected most.',
    ],
  },
  {
    slug: 'what-to-say-to-your-doctor',
    question: 'How do I make my doctor take me seriously?',
    category: 'Being believed',
    standfirst:
      'You should not have to strategise for this. Until the system changes, here is what demonstrably works.',
    readMinutes: 6,
    reviewedBy: 'Nadia Okonkwo, NP',
    reviewedOn: 'June 2026',
    body: [
      {
        heading: 'Bring data, not adjectives',
        paragraphs: [
          '“I am exhausted” is easy to absorb into a general impression. “My cycle has gone from 28 days to 22 days over four months, I wake at 3am five nights a week, and I have had six hot flushes today” is a clinical picture.',
          'Three months of cycle lengths and a week of symptom counts is enough. Our symptom decoder will produce this summary for you at the end.',
        ],
      },
      {
        heading: 'Name the request',
        paragraphs: [
          'Ask a specific question rather than presenting an open problem: “Am I a candidate for transdermal estradiol, and if not, what is the clinical reason?” It is much harder to answer that with reassurance alone.',
          'If the answer is no, ask for the reason to be written in your notes. This is a reasonable request and it changes the conversation.',
        ],
      },
      {
        heading: 'Know when to move on',
        paragraphs: [
          'If you have raised this twice and been offered nothing but an antidepressant and a suggestion to reduce stress, you are not failing to communicate. You are with a clinician who was not trained in this.',
          'Ask for a referral to a menopause specialist, or go directly to one. That is not being difficult. That is standard practice in every other area of medicine.',
        ],
      },
    ],
    unknowns: [
      'Access to menopause-trained clinicians varies enormously by geography and insurance, and we cannot pretend otherwise.',
    ],
  },
]

export function getArticle(slug: string): Article | undefined {
  return articles.find((article) => article.slug === slug)
}

export const articleCategories = [
  'All',
  'Being believed',
  'Perimenopause',
  'Hormone therapy',
  'Body',
  'Mind',
] as const
